import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useTabContext, TabPanel } from '@/contexts/TabContext';
import { SplitSquareHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { Tab } from './Tab';

interface TabBarProps {
  className?: string;
  panel?: TabPanel; // Which panel this tab bar belongs to (defaults to 'left')
}

export const TabBar: React.FC<TabBarProps> = ({ className, panel = 'left' }) => {
  const { 
    tabs, 
    getLeftTabs, 
    getRightTabs, 
    splitViews, 
    switchToTab, 
    switchToRightTab, 
    activePanel, 
    requestTabClose, 
    reorderTabs, 
    createSplitView, 
    closeSplitView,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
  } = useTabContext();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showSplitZone, setShowSplitZone] = useState(false);

  // Get tabs for this panel
  const panelTabs = panel === 'left' ? getLeftTabs() : getRightTabs();
  
  // Check if this panel is the active/focused one
  const isPanelFocused = activePanel === panel;
  
  // Only show split zone in left panel and when not already split
  const canShowSplitZone = panel === 'left' && splitViews.length === 0;

  if (panelTabs.length === 0) {
    return null;
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    if (canShowSplitZone) {
      setShowSplitZone(true);
    }
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      reorderTabs(draggedIndex, dropIndex);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setShowSplitZone(false);
  };

  const handleSplitZoneDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleSplitZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleSplitZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (draggedIndex !== null) {
      const tab = panelTabs[draggedIndex];
      if (tab) {
        createSplitView(tab.id);
      }
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    setShowSplitZone(false);
  };

  // For right panel tabs, closing means closing the split view
  const handleTabClose = (tabId: string) => {
    if (panel === 'right') {
      // Find the split view for this tab and close it
      const splitView = splitViews.find(sv => sv.tabId === tabId);
      if (splitView) {
        closeSplitView(splitView.id);
      }
    } else {
      requestTabClose(tabId);
    }
  };

  // Get the active tab for navigation buttons
  const activeTab = panelTabs.find(t => t.isActive);
  const canNavigateBack = activeTab ? canGoBack(activeTab.id) : false;
  const canNavigateForward = activeTab ? canGoForward(activeTab.id) : false;

  return (
    <div 
      className={cn(
        "h-12 bg-background flex items-end overflow-x-auto overflow-y-hidden relative",
        "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
        className
      )}
    >
      {/* Custom bottom border that gets interrupted by active tabs */}
      <div className="absolute bottom-0 left-0 right-0 h-px border-b-2 border-border z-0" />
      
      {/* Back/Forward Navigation Buttons - only for left panel */}
      {panel === 'left' && (
        <div className="flex items-center gap-1 px-2 h-8 mb-1 z-50">
          <button
            onClick={() => activeTab && goBack(activeTab.id)}
            disabled={!canNavigateBack}
            className={cn(
              "p-1.5 rounded-md transition-colors",
              canNavigateBack 
                ? "hover:bg-muted text-foreground" 
                : "text-muted-foreground/40 cursor-not-allowed"
            )}
            title="Go back (Alt+Left)"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => activeTab && goForward(activeTab.id)}
            disabled={!canNavigateForward}
            className={cn(
              "p-1.5 rounded-md transition-colors",
              canNavigateForward 
                ? "hover:bg-muted text-foreground" 
                : "text-muted-foreground/40 cursor-not-allowed"
            )}
            title="Go forward (Alt+Right)"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      
      <div className="flex min-w-0 ms-2 z-50">
        {/* Tabs for this panel */}
        {panelTabs.map((tab, index) => {
          const isSplitView = panel === 'right';
          
          // Handle tab click based on panel
          const handleTabClick = () => {
            if (panel === 'left') {
              switchToTab(tab.id);
            } else {
              // Right panel tab - update URL to this tab's path
              switchToRightTab(tab.id);
            }
          };
          
          return (
            <Tab
              key={tab.id}
              id={tab.id}
              title={tab.title}
              icon={tab.icon}
              isActive={isSplitView ? true : tab.isActive} // Right panel tabs are always "active" visually
              isSplitView={isSplitView}
              isPanelFocused={isPanelFocused} // Pass panel focus state
              canClose={true} // All tabs can be closed now
              draggable={panel === 'left'} // All left panel tabs are draggable
              draggedIndex={draggedIndex}
              dragOverIndex={dragOverIndex}
              actualIndex={index}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onClick={handleTabClick}
              onClose={() => handleTabClose(tab.id)}
            />
          );
        })}
        
        {/* Split View Drop Zone - only in left panel when no split exists */}
        {canShowSplitZone && showSplitZone && (
          <div
            onDragEnter={handleSplitZoneDragEnter}
            onDragOver={handleSplitZoneDragOver}
            onDrop={handleSplitZoneDrop}
            className={cn(
              "flex items-center justify-center min-w-16 h-8 mx-2 px-3 border-2 border-dashed border-primary",
              "rounded-t-sm bg-primary/10 transition-all duration-200",
              "hover:bg-primary/20"
            )}
          >
            <SplitSquareHorizontal className="h-4 w-4 text-primary" />
          </div>
        )}
      </div>
    </div>
  );
};
